import { Fragment, useState, useMemo, useEffect, useRef } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { TEST_IDS, testId } from '@lib/utils/testIds';
import {
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  DocumentPlusIcon,
  // SUBSCRIPTIONS_DISABLED: CreditCardIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  CodeBracketIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@lib/utils/cn';
import { normalizeRole } from '@lib/utils/roleNavigation';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useIdleTimeout } from '@features/auth/hooks/useIdleTimeout';

import { SidebarCompanyLogo } from '@components/common/SidebarCompanyLogo';
import { TenantSwitcher } from '@features/companies/components/TenantSwitcher';
import { trackingHelpers } from '@/services/mixpanel';
import { useAuthStore } from '@features/auth/store/authStore';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';
import { SupportReportButton, SupportWelcomeWizard } from '@features/support';
import { useCertificateExpiryStatus } from '@features/certificates/hooks/useCertificateExpiryStatus';
import { CertificateExpiryModal } from '@features/certificates/components/CertificateExpiryModal';
import { CertificateExpiryBanner } from '@features/certificates/components/CertificateExpiryBanner';

// Role constants - normalized (uppercase, no spaces)
const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  ADMINISTRATOR: 'ADMINISTRATOR',
  COMPANY_ADMIN: 'COMPANYADMIN',
  // Rol de marca NAOS — mismo nivel de menú que COMPANY_ADMIN en el portal original
  // (ver NAOS_Analisis_Funcionalidad.md §3.2).
  NAOS_ADMINISTRATOR: 'NAOSADMINISTRATOR',
  VENDEDOR: 'VENDEDOR',
  SELLER: 'SELLER',
  SALESPERSON: 'SALESPERSON',
  CUSTOMER_B2B: 'CUSTOMERB2B',
  INTERNAL_ADMIN: 'INTERNALADMIN',
} as const;

// Navigation items with role-based access
// SUPERADMIN: Full access to everything + Company Selector
// ADMINISTRATOR: Full access (including Planes)
// COMPANY ADMIN / NAOS ADMINISTRATOR: Everything EXCEPT Planes y Suscripción
// VENDEDOR / CUSTOMER B2B: Facturas Activas, Nueva Factura, Clientes, Productos, Soporte
const allNavigation: Array<{ name: string; href: string; icon: typeof Squares2X2Icon; roles: string[] | 'all' }> = [
  { name: 'Dashboard', href: '/dashboard', icon: Squares2X2Icon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
  { name: 'Facturas Activas', href: '/home/main', icon: DocumentTextIcon, roles: 'all' },
  { name: 'Archivo Histórico', href: '/home/minor', icon: ArchiveBoxIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
  { name: 'Nueva Factura', href: '/invoices/new', icon: DocumentPlusIcon, roles: 'all' },
  { name: 'Clientes', href: '/customers', icon: UserGroupIcon, roles: 'all' },
  { name: 'Productos', href: '/products', icon: ShoppingBagIcon, roles: 'all' },
  { name: 'Reportes', href: '/report/reports', icon: ChartBarIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
];

const allCompanyNav = [
  { name: 'Mi Empresa', href: '/company/edit', icon: BuildingOfficeIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
  { name: 'Configuración', href: '/company/settings', icon: Cog6ToothIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
];

const allManagementNav = [
  { name: 'Sucursales', href: '/branchoffice/manager', icon: BuildingStorefrontIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
  { name: 'Gestión de Usuarios', href: '/user/manager', icon: UsersIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN, ROLES.NAOS_ADMINISTRATOR] },
  // SUBSCRIPTIONS_DISABLED: { name: 'Planes y Suscripción', href: '/subscriptions/plans', icon: CreditCardIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR] },
  // Igual candado que el backend (requirePolicy('InternalAdmin'), auth.middleware.ts):
  // solo INTERNAL ADMIN, ni siquiera SUPER ADMIN.
  { name: 'Banners', href: '/admin/banners', icon: MegaphoneIcon, roles: [ROLES.INTERNAL_ADMIN] },
  { name: 'Herramientas de Desarrollo', href: '/admin/devtools', icon: CodeBracketIcon, roles: [ROLES.SUPERADMIN] },
];

const allSupportNav = [
  { name: 'Manual de Usuario', href: '/manual', icon: BookOpenIcon, roles: 'all' as const },
  { name: 'Preguntas Frecuentes', href: '/faq', icon: QuestionMarkCircleIcon, roles: 'all' as const },
];

// Helper function to check if user has access to a nav item
const hasAccess = (itemRoles: string[] | 'all', userRole: string | undefined): boolean => {
  if (!userRole) return true;
  if (itemRoles === 'all') return true;
  const normalizedUserRole = normalizeRole(userRole);
  return itemRoles.includes(normalizedUserRole);
};

/**
 * Clases de un item de navegacion sobre el sidebar oscuro
 * (Claude Design · Dashboard.dc.html). La barra teal a la izquierda del item
 * activo es un ::before, definido como utilidad .nav-item-active en index.css.
 */
const navLinkClass = (isActive: boolean, collapsed = false) =>
  cn(
    'group relative flex items-center gap-x-3 rounded-lg px-3.5 py-2 text-[14px] font-medium transition-colors',
    isActive
      ? 'nav-item-active bg-teal-500/[.08] text-white'
      : 'text-white/[.62] hover:bg-white/[.03] hover:text-white',
    collapsed && 'justify-center px-2'
  );

/** Icono del item: teal cuando esta activo. */
const navIconClass = (isActive: boolean) =>
  cn('h-[18px] w-[18px] shrink-0 transition-colors', isActive ? 'text-teal-500' : 'text-white/50 group-hover:text-white/[.85]');

/** Encabezado de grupo del sidebar. */
// `text-white/[.62]` y no `/[.32]`: sobre el azul del sidebar, .32 daba 2.79:1
// y no llegaba al 4.5:1 que pide WCAG AA para texto pequeño. Con .62 sube a
// ~5.6:1 y el rotulo sigue siendo secundario frente al item activo.
const NAV_GROUP = 'px-3.5 text-[11px] font-bold uppercase tracking-[0.24em] text-white/[.62]';

/**
 * Cabecera de cada sidebar: el logo de la compania (`SidebarCompanyLogo`). El
 * `-mx-5` anula el `px-5` del sidebar para que su borde inferior llegue de
 * lado a lado; el componente vuelve a poner el padding por dentro. Mide 64px,
 * los mismos que la barra superior, asi que el menu y el contenido arrancan a
 * la misma altura.
 */
const SIDEBAR_HEADER = 'relative z-[2] -mx-5 shrink-0';

// Helper to generate test ID from navigation item name
const getNavTestId = (itemName: string) => {
  return testId('nav', itemName.toLowerCase().replace(/\s+/g, '-'), 'link');
};

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  useIdleTimeout();
  const tenantInfo = useAuthStore((s) => s.tenantInfo);
  const { data: selectedCompanyData } = useSelectedCompany();

  // Pop-up + banner de certificado por vencer -- a nivel de layout para que
  // aparezcan "de entrada" en CUALQUIER pantalla tras el login, sin depender
  // de que el usuario visite Configuracion > Informacion Fiscal y
  // Certificados (feedback 2026-08-12: un cliente que nunca entra ahi nunca
  // se enteraba de que su certificado vencio). El banner (sin boton de
  // cierre) se renderiza mientras el estado siga Amarillo/Rojo; el pop-up
  // solo se auto-abre una vez por montaje del layout (una vez por sesion),
  // igual que antes en FiscalInfoTab.tsx.
  const certificateExpiry = useCertificateExpiryStatus();
  const [isExpiryModalOpen, setIsExpiryModalOpen] = useState(false);
  const hasAutoOpenedExpiryModalRef = useRef(false);

  useEffect(() => {
    if (hasAutoOpenedExpiryModalRef.current || certificateExpiry.isLoading) {
      return;
    }
    hasAutoOpenedExpiryModalRef.current = true;
    if (certificateExpiry.status === 'yellow' || certificateExpiry.status === 'red') {
      setIsExpiryModalOpen(true);
    }
  }, [certificateExpiry.isLoading, certificateExpiry.status]);

  // 'yellow'/'red' explicito (no 'green') una sola vez, para no repetir el
  // chequeo de status en cada punto donde se usa (banner + pop-up).
  const actionableExpiry: { status: 'yellow' | 'red'; nearestExpiresAt: Date | null } | null =
    certificateExpiry.status === 'green'
      ? null
      : { status: certificateExpiry.status, nearestExpiresAt: certificateExpiry.nearestExpiresAt };
  // Identidad de cliente para analytics (Mixpanel): se registra como
  // super-propiedades + perfil para que TODOS los eventos sean atribuibles a la
  // empresa/persona. Se dispara con el usuario y, cuando está disponible, la
  // compañía seleccionada completa (con code/ruc). Cubre login, auto-selección
  // de compañía, cambio manual de compañía y recarga de página.
  // getSelectedCompany() devuelve `{ company }` (mismo shape que el backend y
  // que TenantSwitcher/useCompanies, que comparten la query key
  // ['company', 'selected'] — ver companyService.ts para el porqué).
  const selectedCompany = selectedCompanyData?.company ?? null;
  useEffect(() => {
    if (!user?.id) return;
    trackingHelpers.registerClientIdentity({
      user: { id: user.id, email: user.email, fullName: user.fullName, role: user.role },
      company: selectedCompany
        ? {
            id: selectedCompany.id,
            name: selectedCompany.name,
            code: selectedCompany.code,
            // En Panamá el RUC suele venir en NumberId; el campo `ruc` puede
            // llegar vacío (ver mapeo en authService/company.controller), por eso
            // se cae a numberId como respaldo para no perder el identificador.
            ruc: selectedCompany.ruc || selectedCompany.numberId,
          }
        : null,
      tenantId: tenantInfo?.identifier || tenantInfo?.id,
    });
  }, [
    user?.id,
    user?.email,
    selectedCompany?.id,
    selectedCompany?.code,
    selectedCompany?.ruc,
    selectedCompany?.numberId,
    tenantInfo?.identifier,
  ]);

  // Filter navigation items based on user role
  const userRole = user?.role;

  const navigation = useMemo(() =>
    allNavigation.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const companyNav = useMemo(() =>
    allCompanyNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const managementNav = useMemo(() =>
    allManagementNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const supportNav = useMemo(() =>
    allSupportNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  /**
   * Miga de pan de la topbar: "Grupo · Item". Se deriva de la ruta activa
   * buscando en los mismos arrays que arman el menu, asi que nunca queda
   * desincronizada ni hay textos hardcodeados por pantalla.
   */
  const breadcrumb = useMemo(() => {
    // Detalle de factura no es un item del menu (se entra desde "Ver detalle"
    // en Facturas Activas / Archivo Histórico), asi que no matchea ningun
    // href de los arrays de abajo. Se cubre a mano para que la topbar no
    // quede en blanco al entrar a esta pantalla.
    if (location.pathname.startsWith('/pages/invoice/')) {
      return 'Principal · Facturas Activas · Detalle de factura';
    }

    const groups: Array<[string, Array<{ name: string; href: string }>]> = [
      ['Principal', navigation],
      ['Empresa', companyNav],
      ['Administración', managementNav],
      ['Soporte', supportNav],
    ];
    for (const [groupName, items] of groups) {
      const match = items.find((i) => location.pathname.startsWith(i.href));
      if (match) return `${groupName} · ${match.name}`;
    }
    return null;
  }, [location.pathname, navigation, companyNav, managementNav, supportNav]);

  const handleNavClick = (itemName: string, href: string, menuType: 'sidebar' | 'mobile') => {
    trackingHelpers.trackNavigationClick(itemName, href, menuType);
  };

  return (
    <div className="min-h-screen bg-[#fafafa] font-display dark:bg-secondary-900">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-secondary-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1" data-testid={TEST_IDS.NAV.SIDEBAR_MOBILE}>
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <div
                  className="relative flex grow flex-col overflow-y-auto px-5 pb-4 text-white"
                  style={{
                    background:
                      'radial-gradient(ellipse 700px 500px at 15% 0%, #143b56 0%, rgba(20,59,86,0) 55%), linear-gradient(180deg, #0d2a3d 0%, #061a26 100%)',
                  }}
                >
                  <div className={SIDEBAR_HEADER}>
                    <SidebarCompanyLogo />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-0">
                      <li>
                        <div className={`${NAV_GROUP} mb-2 mt-6`}>Principal</div>
                        <ul role="list" className="-mx-2 mt-2 space-y-0.5">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                onClick={() => {
                                  handleNavClick(item.name, item.href, 'mobile');
                                  setSidebarOpen(false);
                                }}
                                className={navLinkClass(location.pathname.startsWith(item.href))}
                              >
                                <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                      {companyNav.length > 0 && (
                        <li>
                          <div className={`${NAV_GROUP} mb-2 mt-4`}>Empresa</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-0.5">
                            {companyNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={navLinkClass(location.pathname === item.href)}
                                >
                                  <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                      {managementNav.length > 0 && (
                        <li>
                          <div className={`${NAV_GROUP} mb-2 mt-4`}>Administración</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-0.5">
                            {managementNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={navLinkClass(location.pathname === item.href)}
                                >
                                  <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                      {supportNav.length > 0 && (
                        <li>
                          <div className={`${NAV_GROUP} mb-2 mt-4`}>Soporte</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-0.5">
                            {supportNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={navLinkClass(location.pathname === item.href)}
                                >
                                  <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className={cn(
        "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300",
        sidebarCollapsed ? "lg:w-20" : "lg:w-[260px]"
      )} data-testid={TEST_IDS.NAV.SIDEBAR_DESKTOP}>
        <div
          className="relative flex grow flex-col overflow-y-auto px-5 pb-4 text-white"
          style={{
            background:
              'radial-gradient(ellipse 700px 500px at 15% 0%, #143b56 0%, rgba(20,59,86,0) 55%), linear-gradient(180deg, #0d2a3d 0%, #061a26 100%)',
          }}
        >
          {/* Grano de papel, igual que el hero del login */}
          <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full opacity-[.16] mix-blend-overlay">
            <filter id="sidebar-grain">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
              <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.35 0" />
            </filter>
            <rect width="100%" height="100%" filter="url(#sidebar-grain)" />
          </svg>
          {/*
            La cabecera es el logo del cliente, recortado y adaptado para que
            se apoye en el azul sin recuadro blanco (ver
            `SidebarCompanyLogo` y `lib/utils/logoForDarkBackground`).
          */}
          <div className={SIDEBAR_HEADER}>
            <SidebarCompanyLogo collapsed={sidebarCollapsed} />
          </div>
          <nav className="relative z-[2] flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-0">
              <li>
                {!sidebarCollapsed && (
                  <div className={`${NAV_GROUP} mb-2 mt-6`}>Principal</div>
                )}
                <ul role="list" className="-mx-2 space-y-0.5">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                        className={navLinkClass(location.pathname.startsWith(item.href), sidebarCollapsed)}
                        title={sidebarCollapsed ? item.name : ''}
                        data-testid={getNavTestId(item.name)}
                      >
                        <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                        {!sidebarCollapsed && item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              {companyNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className={`${NAV_GROUP} mb-2 mt-4`}>Empresa</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-0.5">
                    {companyNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={navLinkClass(location.pathname === item.href, sidebarCollapsed)}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
              {managementNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className={`${NAV_GROUP} mb-2 mt-4`}>Administración</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-0.5">
                    {managementNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={navLinkClass(location.pathname === item.href, sidebarCollapsed)}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
              {supportNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className={`${NAV_GROUP} mb-2 mt-4`}>Soporte</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-0.5">
                    {supportNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={navLinkClass(location.pathname === item.href, sidebarCollapsed)}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className={navIconClass(location.pathname.startsWith(item.href))} />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:pl-20" : "lg:pl-[260px]"
      )}>
        {/* Top bar — bloque claro tipo pestaña: sombra que lo separa del
            contenido y esquina inferior DERECHA redondeada. La izquierda va
            recta a proposito: ahi empalma con la cabecera del sidebar, que
            mide los mismos 64px, y las dos forman una sola banda clara a lo
            ancho de la app. Redondeada, dejaba un escalon en la union. */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 bg-[#fafafa]/90 px-4 shadow-[0_8px_24px_-8px_rgba(13,42,61,0.18)] backdrop-blur-xl sm:gap-x-6 sm:px-6 lg:px-8 dark:bg-secondary-800/90 md:rounded-br-2xl">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-secondary-700 dark:text-secondary-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid={TEST_IDS.NAV.MOBILE_MENU_BUTTON}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/*
            Colapsar el sidebar vive ACA, no en su cabecera. Ahi le quitaba 56px
            de ancho al logo (el boton mas su contrapeso para poder centrarlo),
            que es justo la dimension que limita a un logo apaisado. Es tambien
            donde lo esperan: VS Code, Linear y Notion ponen el toggle del panel
            en la barra, no dentro del panel.
          */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="-m-2.5 hidden p-2.5 text-secondary-400 transition-colors hover:text-secondary-700 lg:block dark:text-secondary-500 dark:hover:text-secondary-200"
            title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
            data-testid={TEST_IDS.NAV.SIDEBAR_TOGGLE}
          >
            <span className="sr-only">{sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}</span>
            {sidebarCollapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            )}
          </button>

          {/*
            `min-w-0` en los dos envoltorios flex-1: un item de flex tiene
            min-width:auto, asi que sin esto el `truncate` de la miga de pan no
            puede encogerse y el header entero empuja el ancho de la pagina
            (medido: +143px a 390px en el dashboard, +275px en Gestion de
            Usuarios, que tiene la miga mas larga). El cluster de la derecha va
            `shrink-0` para que se recorte la miga y no el selector de empresa.
          */}
          <div className="flex min-w-0 flex-1 gap-x-4 self-stretch lg:gap-x-6">
            {/* Miga de pan — derivada de la ruta activa, no hardcodeada. */}
            <div className="relative flex min-w-0 flex-1 items-center">
              {breadcrumb && (
                <span className="truncate text-[12px] font-semibold uppercase tracking-[0.24em] text-secondary-400">
                  {breadcrumb}
                </span>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-x-4 lg:gap-x-6">
              {/* Company/Tenant Switcher */}
              <TenantSwitcher />

              {/* User menu */}
              <Menu as="div" className="relative" data-testid={TEST_IDS.NAV.USER_MENU}>
                <Menu.Button className="flex items-center gap-x-2 text-sm">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-850 text-xs font-bold text-white">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <span className="hidden lg:block text-secondary-700 dark:text-secondary-300">
                    {user?.firstName} {user?.lastName}
                  </span>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white dark:bg-secondary-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => logout()}
                            className={cn(
                              active && 'bg-secondary-100 dark:bg-secondary-700',
                              'group flex w-full items-center px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300'
                            )}
                            data-testid={TEST_IDS.NAV.LOGOUT_BUTTON}
                          >
                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                            Cerrar Sesión
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Aviso de certificado por vencer -- persistente, sin boton de
            cierre. Visible en cualquier pantalla mientras el estado siga
            Amarillo/Rojo (ver comentario junto a useCertificateExpiryStatus
            arriba). */}
        {actionableExpiry && (
          <CertificateExpiryBanner
            status={actionableExpiry.status}
            nearestExpiresAt={actionableExpiry.nearestExpiresAt}
          />
        )}

        {/* Page content */}
        <main className="pt-10 pb-12">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* Footer — fijo abajo (siempre visible, sin scroll), pero limitado al
            ancho de la columna de contenido: nunca se superpone al menú. */}
        <footer className={cn(
          'fixed bottom-0 left-0 right-0 z-30 h-8 border-t border-[#eeeeef] bg-[#fafafa]/85 backdrop-blur-xl transition-all duration-300 dark:border-secondary-700 dark:bg-secondary-800/85',
          sidebarCollapsed ? 'lg:left-20' : 'lg:left-[260px]'
        )}>
          <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <p className="truncate whitespace-nowrap text-[11px] leading-none text-secondary-400 dark:text-secondary-500">
              <span className="font-bold text-secondary-600 dark:text-secondary-300">Copyright Hypernova Labs</span>
              {' '}- Facturación Electrónica © 2026 v{__APP_VERSION__}
            </p>
            {selectedCompany?.name && (
              <p className="truncate whitespace-nowrap text-[11px] leading-none text-secondary-400 dark:text-secondary-500">
                <span
                  title={`Tenant ID: ${tenantInfo?.id ?? ''}`}
                  className="cursor-help underline decoration-dotted"
                >
                  {selectedCompany.name}
                </span>
              </p>
            )}
          </div>
        </footer>
      </div>

      {/* Asistente de soporte. Va en el layout autenticado (no en App.tsx) para
          que no aparezca en login ni en las pantallas publicas, y para que tenga
          disponible la compania seleccionada. Ambos componentes no renderizan
          nada si el agente no esta configurado en el BFF. */}
      <SupportReportButton />
      <SupportWelcomeWizard />
      {actionableExpiry && (
        <CertificateExpiryModal
          isOpen={isExpiryModalOpen}
          onClose={() => setIsExpiryModalOpen(false)}
          status={actionableExpiry.status}
          nearestExpiresAt={actionableExpiry.nearestExpiresAt}
        />
      )}
    </div>
  );
}
